import type { Socket } from "socket.io-client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

import type { Notification } from "@/store/notification";

import useAuthStore from "@/store/auth";
import { useNotificationStore } from "@/store/notification";

const SocketContext = createContext<Socket | null>(null);

export function useSocket() {
	if (!SocketContext) {
		throw new Error("useSocket must be used within a SocketProvider");
	}
	return useContext(SocketContext);
}

const SOCKET_URL = `${import.meta.env.VITE_BASE_URL?.replace("/v1", "")}/notifications`;
const NOTIFICATION_SOUND = "/audio/notification.mp3";

function useNotificationChime() {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const unlockedRef = useRef(false);

	useEffect(() => {
		const unlock = () => {
			if (unlockedRef.current)
				return;
			const audio = audioRef.current ?? new Audio(NOTIFICATION_SOUND);
			audio.preload = "auto";
			audioRef.current = audio;
			unlockedRef.current = true;
			audio.muted = true;
			void audio.play().then(() => {
				audio.pause();
				audio.currentTime = 0;
				audio.muted = false;
			}).catch(() => {
				audio.muted = false;
			});
		};

		window.addEventListener("pointerdown", unlock);
		window.addEventListener("keydown", unlock);
		return () => {
			window.removeEventListener("pointerdown", unlock);
			window.removeEventListener("keydown", unlock);
		};
	}, []);

	return () => {
		const audio = audioRef.current;
		if (!audio || !unlockedRef.current)
			return;
		audio.currentTime = 0;
		void audio.play().catch(() => {});
	};
}

let sharedSocket: Socket | null = null;
let sharedToken: string | undefined;

function disconnectSharedSocket() {
	if (!sharedSocket)
		return;
	sharedSocket.removeAllListeners();
	sharedSocket.disconnect();
	sharedSocket = null;
	sharedToken = undefined;
}

function getNotificationSocket(accessToken?: string) {
	if (!accessToken) {
		disconnectSharedSocket();
		return null;
	}

	if (sharedSocket && sharedToken === accessToken)
		return sharedSocket;

	disconnectSharedSocket();
	sharedToken = accessToken;

	// extraHeaders only work on HTTP polling in the browser and force a CORS
	// preflight on every poll. Send the token in the handshake instead and
	// prefer a single WebSocket.
	sharedSocket = io(SOCKET_URL, {
		autoConnect: true,
		forceNew: false,
		reconnection: true,
		reconnectionAttempts: 8,
		reconnectionDelay: 2000,
		reconnectionDelayMax: 30_000,
		timeout: 15_000,
		transports: ["websocket", "polling"],
		upgrade: true,
		auth: {
			token: accessToken,
			authorization: `Bearer ${accessToken}`
		}
	});

	return sharedSocket;
}

export function SocketProvider({ children }: { children: React.ReactNode }) {
	const accessToken = useAuthStore(state => state.tokens?.accessToken);
	const addNotification = useNotificationStore(state => state.addNotification);
	const [socket, setSocket] = useState<Socket | null>(null);
	const playChime = useNotificationChime();
	const playRef = useRef(playChime);
	const addNotificationRef = useRef(addNotification);

	playRef.current = playChime;
	addNotificationRef.current = addNotification;

	useEffect(() => {
		const next = getNotificationSocket(accessToken);
		setSocket(next);
		if (!next)
			return;

		const onNotification = (notification: Notification) => {
			addNotificationRef.current(notification);
			playRef.current();
		};

		const subscribe = () => {
			next.emit("SUBSCRIBE_TO_NOTIFICATIONS");
		};

		next.on("NEW_NOTIFICATION", onNotification);
		next.on("connect", subscribe);
		if (next.connected)
			subscribe();

		return () => {
			next.off("NEW_NOTIFICATION", onNotification);
			next.off("connect", subscribe);
		};
	}, [accessToken]);

	return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
}
